import {
  FISCAL_UFS,
  MENU_PATH,
  PRODUCT_TYPES,
  PRODUCT_TYPE_LABEL,
  formatDocument,
  formatMoney,
  formatQuantity,
  type Page,
  type Party,
  type ProductListRow,
  type Salesperson,
} from "@cafegestor/shared";
import { apiFetch } from "@/lib/api";
import type { LookupQuery, LookupSource } from "./types";

const STATUS_LABEL = (s: string) => (s === "active" ? "Ativo" : "Inativo");

function params(q: LookupQuery, extra: Record<string, string | undefined> = {}) {
  const p = new URLSearchParams({
    q: q.q.trim(),
    limit: String(q.limit),
    offset: String(q.offset),
    status: q.includeInactive ? "all" : "active",
  });
  for (const [k, v] of Object.entries({ ...extra, ...q.filters })) if (v) p.set(k, v);
  return p.toString();
}

const cityUf = (p: Pick<Party, "city" | "uf">) => [p.city, p.uf].filter(Boolean).join("/") || "—";

function partySource(papel: "cliente" | "fornecedor"): LookupSource<Party> {
  const noun = papel;
  return {
    key: ["cadastros", "pessoas", "lookup", papel],
    noun,
    placeholder: `Código, nome ou CPF/CNPJ do ${noun}`,
    empty: `Nenhum ${noun} encontrado. Cadastre em ${
      papel === "cliente" ? MENU_PATH.clientes : MENU_PATH.fornecedores
    }.`,
    search: (q, signal) =>
      apiFetch<Page<Party>>(`/cadastros/pessoas?${params(q, { papel })}`, { signal }),
    toRow: (p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      secondary: [p.document ? formatDocument(p.document) : null, cityUf(p)]
        .filter(Boolean)
        .join(" · "),
      inactive: p.status !== "active",
    }),
    columns: [
      { header: "Código", cell: (p) => p.code, className: "font-mono text-xs" },
      { header: "Razão social / nome", cell: (p) => p.name },
      { header: "Nome fantasia", cell: (p) => p.tradeName ?? "—" },
      { header: "CPF/CNPJ", cell: (p) => (p.document ? formatDocument(p.document) : "—") },
      { header: "Cidade/UF", cell: (p) => cityUf(p) },
      { header: "Situação", cell: (p) => STATUS_LABEL(p.status) },
    ],
    filters: [{ name: "uf", label: "UF", options: FISCAL_UFS.map((u) => [u, u]) }],
  };
}

export const customerSource = partySource("cliente");
export const supplierSource = partySource("fornecedor");

/** Produtos; na venda, com o disponível no depósito e o preço da tabela do cliente. */
export function productSource(
  sale: { warehouseId?: string; customerId?: string } = {},
): LookupSource<ProductListRow> {
  const inSale = Boolean(sale.warehouseId || sale.customerId);
  return {
    key: ["cadastros", "produtos", "lookup", sale.warehouseId ?? "", sale.customerId ?? ""],
    noun: "produto",
    placeholder: "Código, descrição ou código de barras",
    empty: `Nenhum produto encontrado. Cadastre em ${MENU_PATH.produtos}.`,
    search: (q, signal) =>
      apiFetch<Page<ProductListRow>>(
        `/cadastros/produtos?${params(q, {
          warehouseId: sale.warehouseId,
          customerId: sale.customerId,
        })}`,
        { signal },
      ),
    toRow: (p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      secondary: [
        p.baseUnitCode,
        p.available !== undefined
          ? `disponível ${formatQuantity(p.available)} ${p.baseUnitCode}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      inactive: p.status !== "active",
    }),
    columns: [
      { header: "Código", cell: (p) => p.code, className: "font-mono text-xs" },
      { header: "Descrição", cell: (p) => p.name },
      { header: "Unidade", cell: (p) => p.baseUnitCode },
      ...(inSale
        ? [
            {
              header: "Preço da tabela",
              cell: (p: ProductListRow) =>
                p.listPrice === null ? "fora da tabela" : formatMoney(p.listPrice),
            },
            {
              header: "Disponível",
              cell: (p: ProductListRow) =>
                p.available === undefined
                  ? "—"
                  : `${formatQuantity(p.available)} ${p.baseUnitCode}`,
            },
          ]
        : []),
      { header: "Situação", cell: (p) => STATUS_LABEL(p.status) },
    ],
    filters: [
      {
        name: "tipo",
        label: "Tipo",
        options: PRODUCT_TYPES.map((t) => [t, PRODUCT_TYPE_LABEL[t]]),
      },
    ],
  };
}

export const salespersonSource: LookupSource<Salesperson> = {
  key: ["cadastros", "vendedores", "lookup"],
  noun: "vendedor",
  placeholder: "Código ou nome do vendedor",
  empty: "Nenhum vendedor encontrado.",
  search: (q, signal) =>
    apiFetch<Page<Salesperson>>(`/cadastros/vendedores?${params(q)}`, { signal }),
  toRow: (s) => ({
    id: s.id,
    code: s.code,
    name: s.name,
    ...(s.userEmail ? { secondary: s.userEmail } : {}),
    inactive: s.status !== "active",
  }),
  columns: [
    { header: "Código", cell: (s) => s.code, className: "font-mono text-xs" },
    { header: "Nome", cell: (s) => s.name },
    { header: "Usuário ligado", cell: (s) => s.userEmail ?? "—" },
    { header: "Situação", cell: (s) => STATUS_LABEL(s.status) },
  ],
};
